import { Button, Dialog, DialogContent, DialogTrigger, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { isFeatureEnabled, ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { useActionPermission } from '~/authorizations/useActionPermission';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import rootStore from '~/store';
import { EXPORT_TAB_ID } from '../exportTabIds';
import { asbuiltConfig, type CSVExportProps, config, downloadCSV, generateCSV } from './csvExport';
import store, { type AssetLayer, type RequestableLayer } from './store';
import RequestableTable from './tables/RequestableTable';
import StandardTable from './tables/StandardTable';
import UtilityContactsModal from './UtilityContactsModal';

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const [contactsOpen, setContactsOpen] = useState(false);

  const { isLoading, currentlyAvailable, noKnownAssets, dataRequestable, asbuiltRequestable, project } = useSnapshot(
    store,
  ) as typeof store;

  const { inUnverifiedMode, currentOrganization } = useSnapshot(rootStore) as typeof rootStore;

  const navigate = useNavigate();

  const canDownloadData = useActionPermission(Action.DownloadData);
  const downloadsEnabled =
    canDownloadData &&
    !!project &&
    isFeatureEnabled(ProjectFeature.Downloads, project.billingTier, currentOrganization?.subscription);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    // need this loading state here to re-initialize loading state when switching projects.
    store.isLoading = true;
    store.getUtilityData(projectId);
  }, []);

  const exportOutreachLog = () => {
    const AS_BUILT = 'isAsBuilt';

    const _asbuiltRequestable: any = asbuiltRequestable.map((req) => {
      const r: any = { ...req };
      r[AS_BUILT] = true;
      return r;
    });

    const allAssets = [...currentlyAvailable, ...noKnownAssets, ...dataRequestable, ..._asbuiltRequestable];

    const findAsset = (assets: (RequestableLayer | AssetLayer)[], id: string) => {
      return assets.find((a) => a.id && id && a.id.toString() === id.toString());
    };

    const rows: CSVExportProps[] = [];

    allAssets.forEach((a) => {
      const { name, type, id } = a;
      const ca = findAsset(currentlyAvailable, id) ? 'X' : '';
      const dr = findAsset(dataRequestable, id) ? 'X' : '';
      const ar = findAsset(asbuiltRequestable, id) ? 'X' : '';

      const row: { [key: string]: any } = {
        name: name ? name.replaceAll(',', '') : name,
        type,
        currentlyAvailable: ca,
        dataRequestable: dr,
        asbuiltRequestable: ar,
      };

      Object.keys(config).forEach((key) => {
        if ((a as any)[AS_BUILT] === true) {
          // skip
        } else {
          const { property, getValue } = config[key];
          const v = getValue(a);
          row[property] = v;
        }
      });

      Object.keys(asbuiltConfig).forEach((key) => {
        if ((a as any)[AS_BUILT] === true) {
          // temp - add spaces to beginning for CSV. Remove this to run excel export.
          // /*
          Object.keys(config).forEach((key) => {
            row[key] = '';
          });
          // */

          const { property, getValue } = asbuiltConfig[key];
          const v = getValue(a);
          row[property] = v;
        }
      });

      rows.push(row);
    });

    const csv = generateCSV(rows);
    downloadCSV(csv, project?.name || 'Project');

    capturePosthogEvent(POSTHOG_EVENTS.utilitiesOutreachLogExported, {
      project_id: projectId,
    });
  };

  const OutreachButton = () => (
    <div className="w-[175px] flex-shrink-0 pl-2">
      <Can action={Action.ExportOutreachLog}>
        <Button onClick={exportOutreachLog} data-testid="utilities-details-export-outreach">
          Export Outreach Log
        </Button>
      </Can>
    </div>
  );

  const utilitiesTabTables = [
    {
      title: 'Available Digitally',
      description: 'Utility Assets in Project Area Available Digitally',
      rows: store.currentlyAvailable,
      button: downloadsEnabled ? (
        <Button
          onClick={() => navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId: EXPORT_TAB_ID } })}
          data-testid="utilities-details-download-data"
        >
          Download Data
        </Button>
      ) : undefined,
      type: 'standard',
      notes: 'The line data for the Fiber Routes layer is snapped to the road centerline.',
    },
    {
      title: 'No Known Assets',
      description: `Per the agencies' data, there are no known assets in this area.`,
      rows: store.noKnownAssets,
      type: 'standard',
    },
    {
      title: 'Request Maps',
      description: 'Request Maps for the Utility Assets in the Projects Area not Available',
      rows: store.dataRequestable.filter(({ type }) => !['Telco', 'Fiber', 'Cable'].includes(type)),
      tableType: 'dataRequested',
      button: <OutreachButton />,
      type: 'requestable',
      notes:
        'CivilGrid will handle utility data requests on your behalf. There will be a turnaround time associated with any map requests. You will be alerted of and asked to approve of any data retrieval fees charged by agencies, for which you will be responsible.',
    },
    {
      title: 'Request Telco Maps',
      description:
        'Our fiber data generally includes only the trunk lines running in the public right-of-way. If you need more detail, you can request fiber detail maps from the agencies. Generally, cable and copper lines are not digitized.',
      tableType: 'fiberDetailRequested',
      rows: store.dataRequestable.filter(({ type }) => ['Fiber', 'Telco', 'Cable'].includes(type)),
      button: <OutreachButton />,
      type: 'requestable',
    },
    {
      title: 'Request As-Builts',
      description: 'Request As-Builts for the Utility Assets in the Projects Area.',
      rows: store.asbuiltRequestable,
      tableType: 'asbuiltRequested',
      type: 'requestable',
      notes:
        'Not all agencies provide as-builts, and as-built requests are often processed more slowly than map requests. We will notify you if issues arise with fulfilling your request.',
    },
  ];

  if (isLoading) {
    return <SkeletonSpinnerCentered />;
  }

  return (
    <div className="w-full" data-testid="utilities-details-page">
      <div className="flex space-x-2 py-2">
        <Dialog open={contactsOpen} onOpenChange={setContactsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="utilities-details-view-contacts">View Utility Contacts</Button>
          </DialogTrigger>
          <DialogContent
            title="View Utility Contacts"
            className="z-[110] w-auto min-w-min max-w-[75vw]"
            overlayClassName="z-[100]"
            data-testid="utilities-details-contacts-dialog"
          >
            <UtilityContactsModal closeDialog={() => setContactsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-8">
        {utilitiesTabTables.map((table) => {
          const testId = `utilities-details-table-${table.title.toLowerCase().replace(/\s+/g, '-')}`;
          if (table.type === 'standard') {
            return (
              <StandardTable
                key={table.title}
                title={table.title}
                description={table.description}
                rows={table.rows as AssetLayer[]}
                button={table.button}
                notes={table.notes || ''}
                data-testid={testId}
              />
            );
          } else {
            return (
              <RequestableTable
                key={table.title}
                title={table.title}
                description={table.description}
                rows={table.rows as RequestableLayer[]}
                tableType={table.tableType as 'dataRequested' | 'asbuiltRequested'}
                button={table.button}
                notes={table.notes || ''}
                data-testid={testId}
              />
            );
          }
        })}
      </div>
    </div>
  );
}
